import * as React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useAuth } from '../../Context/AuthContext'
import { toast } from 'react-toastify'

const STEP_LABELS = ['Request Reset', 'Verify Code', 'New Password']

const ForgotPasswordModal = ({ open, onClose }) => {
  const { requestPasswordReset, verifyPasswordResetCode, completePasswordReset } = useAuth()
  const [step, setStep] = React.useState(1)
  const [email, setEmail] = React.useState('')
  const [code, setCode] = React.useState('')
  const [resetToken, setResetToken] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [resending, setResending] = React.useState(false)
  const [error, setError] = React.useState('')
  const [countdown, setCountdown] = React.useState(0)
  const timerRef = React.useRef(null)

  const startCountdown = (seconds = 60) => {
    clearInterval(timerRef.current)
    setCountdown(seconds)
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  React.useEffect(() => () => clearInterval(timerRef.current), [])

  const reset = () => {
    setStep(1)
    setEmail('')
    setCode('')
    setResetToken('')
    setNewPassword('')
    setConfirmPassword('')
    setError('')
    setShowPassword(false)
    setCountdown(0)
    clearInterval(timerRef.current)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleStep1 = async () => {
    if (!email.trim()) {
      setError('Please enter your email address.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await requestPasswordReset(email.trim())
      toast.success('Reset code sent. Check your email.')
      setStep(2)
      startCountdown(60)
    } catch (err) {
      setError(err.message || 'Failed to send reset email.')
    } finally {
      setLoading(false)
    }
  }

  const handleStep2 = async () => {
    if (!code.trim()) {
      setError('Please enter the verification code.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const result = await verifyPasswordResetCode(email.trim(), code.trim())
      // Server may return a token to use in step 3; fall back to the code itself
      setResetToken(result?.token || result?.resetToken || code.trim())
      setStep(3)
    } catch (err) {
      setError(err.message || 'Invalid or expired code.')
    } finally {
      setLoading(false)
    }
  }

  const handleStep3 = async () => {
    if (!newPassword) {
      setError('Please enter a new password.')
      return
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await completePasswordReset(email.trim(), resetToken, newPassword, confirmPassword)
      toast.success('Password reset successfully. Please sign in.')
      handleClose()
    } catch (err) {
      setError(err.message || 'Failed to reset password.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    setError('')
    try {
      await requestPasswordReset(email.trim())
      toast.success('Reset code resent. Check your email.')
      startCountdown(60)
    } catch (err) {
      setError(err.message || 'Failed to resend code.')
    } finally {
      setResending(false)
    }
  }

  const stepAction = step === 1 ? handleStep1 : step === 2 ? handleStep2 : handleStep3
  const stepLabel = step === 1 ? 'Send Code' : step === 2 ? 'Verify' : 'Reset Password'

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Forgot Password
        <Typography variant="caption" display="block" color="text.secondary">
          Step {step} of 3 — {STEP_LABELS[step - 1]}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {step === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your email address and we'll send you a reset code.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleStep1()}
              error={!!error}
              helperText={error}
              disabled={loading}
            />
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the code sent to <strong>{email}</strong>.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Verification Code"
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleStep2()}
              error={!!error}
              helperText={error}
              disabled={loading}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <Button
                size="small"
                onClick={() => { setStep(1); setError(''); setCountdown(0); clearInterval(timerRef.current) }}
                disabled={loading || resending}
              >
                Back
              </Button>
              {countdown > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Resend in <strong>{countdown}s</strong>
                </Typography>
              ) : (
                <Button
                  size="small"
                  onClick={handleResend}
                  disabled={resending || loading}
                >
                  {resending ? 'Resending…' : 'Resend Code'}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Choose a new password for your account.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(s => !s)} edge="end" size="small">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleStep3()}
              error={!!error}
              helperText={error}
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={stepAction}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Please wait…' : stepLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ForgotPasswordModal
